// src/pages/feedback/FeedbackOverview.jsx
import React, { useEffect, useState, useMemo } from "react";
import { Row, Col, Card, Spin, Dropdown, Button, Table, Tag, Empty } from "antd";
import { DownOutlined } from "@ant-design/icons";
import moment from "moment";
import SurveySelect from "../components/SurveySelect";
import exportService from "../../../pages/pos/utils/exportService";
import PrintReportPreview from "../../../pages/pos/reports/PrintReportPreview";

const FeedbackOverview = () => {
  const [loading, setLoading] = useState(false);
  const [surveyId, setSurveyId] = useState(null);
  const [summary, setSummary] = useState(null);
  const [pdfVisible, setPdfVisible] = useState(false);

  // 🔹 Mock Data (replace with API fetch later)
  const mockSummary = {
    totalReviews: 256,
    averageRating: 4.2,
    nps: 45,
    csat: 82,
    promoters: 150,
    detractors: 56,
    passives: 50,
    positivePct: 65,
    negativePct: 20,
    categoryAverages: {
      "Staff Friendliness": 4.5,
      "Check-in Efficiency": 3.9,
      Cleanliness: 4.1,
      "Likelihood to Recommend (NPS)": "45%",
    },
    comments: [
      {
        date: "2025-09-18",
        guest: "John Doe",
        comment: "Great service, loved it!",
        sentiment: "positive",
      },
      {
        date: "2025-09-17",
        guest: "Jane Smith",
        comment: "Room wasn’t clean.",
        sentiment: "negative",
      },
      {
        date: "2025-09-15",
        guest: "Alice Johnson",
        comment: "Check-in took too long.",
        sentiment: "negative",
      },
    ],
    trend: [
      { week: "Sep 01–07", avgRating: 4.3, nps: "48%", responses: 50 },
      { week: "Sep 08–14", avgRating: 4.1, nps: "42%", responses: 65 },
      { week: "Sep 15–21", avgRating: 4.4, nps: "50%", responses: 70 },
    ],
  };

  // Load mock for now
  useEffect(() => {
    setLoading(true);
    setTimeout(() => {
      setSummary(mockSummary);
      setLoading(false);
    }, 500);
  }, [surveyId]);

  // Export data
  const exportData = useMemo(() => {
    if (!summary) return [];
    return [
      { metric: "Total Reviews", value: summary.totalReviews },
      { metric: "Average Rating", value: `${summary.averageRating} ⭐` },
      { metric: "NPS", value: `${summary.nps}%` },
      { metric: "CSAT", value: `${summary.csat}%` },
      { metric: "Promoters", value: summary.promoters },
      { metric: "Detractors", value: summary.detractors },
      { metric: "Passives", value: summary.passives },
      { metric: "Positive %", value: `${summary.positivePct}%` },
      { metric: "Negative %", value: `${summary.negativePct}%` },
    ];
  }, [summary]);

  const headers = [
    { key: "metric", title: "Metric" },
    { key: "value", title: "Value" },
  ];

  const suffix = `_${moment().format("YYYYMMDD")}`;

  const handleExportCSV = () =>
    exportService.exportToCSV(exportData, headers, `overview${suffix}.csv`);

  const handleExportXLS = () =>
    exportService.exportToXLS(exportData, headers, `overview${suffix}.xlsx`, "Overview");

  const handleExportPDF = () =>
    exportService.exportToPDF(
      exportData,
      headers,
      "Feedback Overview",
      `overview${suffix}.pdf`
    );

  const downloadMenu = (
    <Dropdown.Menu>
      <Dropdown.Item key="csv" onClick={handleExportCSV}>
        Download CSV
      </Dropdown.Item>
      <Dropdown.Item key="xls" onClick={handleExportXLS}>
        Download XLS
      </Dropdown.Item>
      <Dropdown.Item key="pdf" onClick={handleExportPDF}>
        Download PDF
      </Dropdown.Item>
    </Dropdown.Menu>
  );

  // KPI Card helper
  const renderKpiCard = (title, value, color) => (
    <Card
      hoverable
      style={{
        borderRadius: 12,
        background: color,
        color: "#fff",
        textAlign: "center",
      }}
    >
      <h4 style={{ marginBottom: 8, color: "#fff" }}>{title}</h4>
      <h2 style={{ fontWeight: "bold", fontSize: 24, color: "#fff" }}>{value}</h2>
    </Card>
  );

  // Table Configs
  const commentsColumns = [
    {
      title: "Date",
      dataIndex: "date",
      key: "date",
      render: (d) => moment(d).format("DD MMM YYYY"),
    },
    { title: "Guest", dataIndex: "guest", key: "guest" },
    { title: "Comment", dataIndex: "comment", key: "comment" },
    {
      title: "Sentiment",
      dataIndex: "sentiment",
      key: "sentiment",
      render: (val) => (
        <Tag color={val === "positive" ? "green" : "red"}>
          {val.toUpperCase()}
        </Tag>
      ),
    },
  ];

  const trendColumns = [
    { title: "Week", dataIndex: "week", key: "week" },
    { title: "Avg Rating", dataIndex: "avgRating", key: "avgRating" },
    { title: "NPS", dataIndex: "nps", key: "nps" },
    { title: "Responses", dataIndex: "responses", key: "responses" },
  ];

  return (
    <div className="page-wrapper-full">
      <div className="content container-fluid">
        <Spin spinning={loading}>
          {/* Header */}
          <div className="page-header d-flex justify-content-between align-items-center mb-3">
            <div className="d-flex align-items-center gap-3">
              <h3 className="page-title mb-0">Feedback Overview</h3>
              <div style={{ minWidth: "220px" }}>
                <SurveySelect
                  value={surveyId}
                  onChange={(option) => setSurveyId(option?.value || null)}
                  autoSelectFirst
                />
              </div>
            </div>
            <Dropdown overlay={downloadMenu} trigger={["click"]}>
              <Button type="primary">
                Download <DownOutlined />
              </Button>
            </Dropdown>
          </div>

          {!summary ? (
            <Empty description="Select a survey to view feedback overview" />
          ) : (
            <>
              {/* KPI Cards */}
              <Row gutter={16} className="mb-4">
                <Col span={6}>
                  {renderKpiCard("Total Reviews", summary.totalReviews, "linear-gradient(135deg, #3b82f6, #60a5fa)")}
                </Col>
                <Col span={6}>
                  {renderKpiCard("Average Rating", `${summary.averageRating} ⭐`, "linear-gradient(135deg, #52c41a, #73d13d)")}
                </Col>
                <Col span={6}>
                  {renderKpiCard("NPS", `${summary.nps}%`, "linear-gradient(135deg, #faad14, #fadb14)")}
                </Col>
                <Col span={6}>
                  {renderKpiCard("CSAT", `${summary.csat}%`, "linear-gradient(135deg, #722ed1, #9254de)")}
                </Col>
              </Row>

              {/* Breakdown Tables */}
              <Row gutter={16}>
                <Col span={12}>
                  <Card title="Survey Summary">
                    <Table
                      dataSource={exportData}
                      columns={headers.map((h) => ({
                        title: h.title,
                        dataIndex: h.key,
                        key: h.key,
                      }))}
                      pagination={false}
                      rowKey="metric"
                      size="small"
                    />
                  </Card>
                </Col>
                <Col span={12}>
                  <Card title="Category Breakdown">
                    <Table
                      dataSource={Object.entries(summary.categoryAverages).map(
                        ([category, value]) => ({
                          category,
                          avgRating: value,
                          responses: summary.totalReviews,
                        })
                      )}
                      columns={[
                        { title: "Category", dataIndex: "category", key: "category" },
                        { title: "Avg Rating", dataIndex: "avgRating", key: "avgRating" },
                        { title: "Responses", dataIndex: "responses", key: "responses" },
                      ]}
                      pagination={false}
                      rowKey="category"
                      size="small"
                    />
                  </Card>
                </Col>
              </Row>

              {/* Comments & Trend */}
              <Row gutter={16} className="mt-4">
                <Col span={12}>
                  <Card title="Recent Comments">
                    <Table
                      dataSource={summary.comments}
                      columns={commentsColumns}
                      pagination={{ pageSize: 3 }}
                      rowKey="date"
                      size="small"
                    />
                  </Card>
                </Col>
                <Col span={12}>
                  <Card title="Trend (Weekly)">
                    <Table
                      dataSource={summary.trend}
                      columns={trendColumns}
                      pagination={false}
                      rowKey="week"
                      size="small"
                    />
                  </Card>
                </Col>
              </Row>

              {/* PDF Preview */}
              <PrintReportPreview
                visible={pdfVisible}
                onClose={() => setPdfVisible(false)}
                reportTitle="Feedback Overview"
                propertyName="Your Property"
                data={exportData}
                headers={headers}
                footerData={[]}
                totalColumns={[]}
                onDownloadPDF={handleExportPDF}
                apiEndpoint="overview/generate-pdf"
                apiUrl=""
              />
            </>
          )}
        </Spin>
      </div>
    </div>
  );
};

export default FeedbackOverview;
